"use client";

import * as React from "react";
import Link from "next/link";
import { motion } from "motion/react";
import { Mic, Briefcase, Lock, PenLine, MessageSquare, Users, ArrowRight, Award, Crown } from "lucide-react";
import { Section } from "@/components/layout/section";
import { SectionHeader } from "@/components/ui/section-header";
import { Badge } from "@/components/ui/badge";

const SERVICES = [
  { icon: Mic, title: "Keynote Speaking", body: "Keynotes for organizations, conferences, universities, and leadership forums. Each engagement is tailored to the audience and the stakes. Stages have included the Nobel Peace Prize and WEF.", href: "/speaking/", gradient: "from-red/20 to-red/0", accent: "text-red", badge: "Most Booked" },
  { icon: Briefcase, title: "Executive Advisory", body: "Operational and governance advisory to founders, executives, and institutions. Board and advisory-board service for organizations seeking the operating discipline that produces verified outcomes.", href: "/services/executive-advisory/", gradient: "from-purple/20 to-purple/0", accent: "text-purple" },
  { icon: Lock, title: "Operational Engagements", body: "Discreet, high-stakes operational and governance work for governments, leaders, and organizations. Available by introduction only. Full operational ecosystem under absolute confidentiality.", href: "/services/operational-consulting/", gradient: "from-emerald/20 to-emerald/0", accent: "text-emerald", badge: "By Introduction" },
  { icon: PenLine, title: "Writing & Thought Leadership", body: "The Operator's Notebook — a biweekly publication on how excellent things run — plus longer essays on execution, leadership, women in operations, and the discipline of building.", href: "/the-operators-notebook/", gradient: "from-amber-gold/20 to-amber-gold/0", accent: "text-amber-gold" },
  { icon: MessageSquare, title: "Press & Media Commentary", body: "Expert commentary for journalists and media organizations on operational excellence, women in leadership, governance transformation, and strategic intelligence.", href: "/services/press-media-commentary/", gradient: "from-indigo/20 to-indigo/0", accent: "text-indigo" },
  { icon: Users, title: "Corporate Workshops", body: "Team training and development programs focused on operational excellence frameworks, evidence-based leadership, and building high-performance diverse teams. Clinical-grade curricula.", href: "/services/corporate-workshops/", gradient: "from-cyan/20 to-cyan/0", accent: "text-cyan" },
];

export function ServicesSection() {
  return (
    <Section id="services" ariaLabel="Services and Engagements" background="muted" pattern>
      <SectionHeader
        icon={Award}
        overline="Services"
        overlineClassName="text-amber-gold"
        pillClassName="glass-tinted-amber-gold border-amber-gold/20"
        title={
          <>
            Engagements Calibrated to the <span className="text-gradient-premium">Stakes.</span>
          </>
        }
        subtitle="Six engagement modalities. Every engagement follows the 6-stage Sovereign Operations Architecture — consistency, accountability, verified results."
      />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5">
        {SERVICES.map((s, i) => (
          <motion.div
            key={s.title}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-30px" }}
            transition={{ duration: 0.5, delay: (i % 3) * 0.08 }}
          >
            <Link href={s.href} className="block h-full group">
              <div className={`relative glass-card rounded-2xl p-6 h-full overflow-hidden bg-gradient-to-br ${s.gradient}`}>
                {s.badge && (
                  <div className="absolute top-4 right-4 z-elevated">
                    <Badge className="bg-gradient-sovereign text-white border-0 text-caption">{s.badge}</Badge>
                  </div>
                )}
                <div aria-hidden className="absolute -top-16 -right-16 size-48 rounded-full bg-current opacity-[0.05] blur-2xl group-hover:scale-150 transition-transform duration-700" />
                <div className="relative">
                  <div className="size-14 rounded-2xl bg-gradient-sovereign flex items-center justify-center shadow-xl mb-5 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300">
                    <s.icon className="size-7 text-white" />
                  </div>
                  <h3 className="text-heading-md mb-3">{s.title}</h3>
                  <p className="text-body-sm text-muted-foreground leading-relaxed mb-4">{s.body}</p>
                  <div className={`inline-flex items-center gap-1.5 text-body-sm font-semibold ${s.accent} group-hover:gap-2.5 transition-all`}>
                    Learn more <ArrowRight className="size-4" />
                  </div>
                </div>
              </div>
            </Link>
          </motion.div>
        ))}
      </div>

      <div className="mt-10 flex flex-wrap items-center justify-center gap-2">
        {[
          { cls: "text-lime", label: "Tailored" },
          { cls: "text-denim", label: "Discreet" },
          { cls: "text-amber-gold", label: "Verified" },
        ].map((s) => (
          <div key={s.label} className="glass rounded-full px-4 py-2 flex items-center gap-2">
            <span className={`${s.cls} text-sm`}>◆</span>
            <span className="text-caption text-muted-foreground">{s.label}</span>
          </div>
        ))}
      </div>
    </Section>
  );
}
